import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [lockoutTime, setLockoutTime] = useState(() => {
    const until = localStorage.getItem('auth_lockout_until');
    if (until) {
      const remaining = Math.ceil((parseInt(until, 10) - Date.now()) / 1000);
      return remaining > 0 ? remaining : 0;
    }
    return 0;
  });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // Clean up expired attempts and sync lockout on mount
    const now = Date.now();
    const saved = JSON.parse(localStorage.getItem('auth_attempts') || '[]');
    const active = saved.filter((timestamp) => now - timestamp < 60000);
    localStorage.setItem('auth_attempts', JSON.stringify(active));

    const until = localStorage.getItem('auth_lockout_until');
    if (until) {
      const remaining = Math.ceil((parseInt(until, 10) - now) / 1000);
      if (remaining > 0) {
        setLockoutTime(remaining);
        setError('Too many attempts. Please try again in 1 minute.');
      } else {
        localStorage.removeItem('auth_lockout_until');
      }
    }
  }, []);

  useEffect(() => {
    let timer = null;
    if (lockoutTime > 0) {
      timer = setInterval(() => {
        setLockoutTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            localStorage.removeItem('auth_lockout_until');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (timer) clearInterval(timer);
    };
  }, [lockoutTime]);

  const checkRateLimit = () => {
    const now = Date.now();
    const saved = JSON.parse(localStorage.getItem('auth_attempts') || '[]');
    const activeAttempts = saved.filter((timestamp) => now - timestamp < 60000);
    return activeAttempts;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 1. Lockout check
    const until = localStorage.getItem('auth_lockout_until');
    if (until) {
      const remaining = Math.ceil((parseInt(until, 10) - Date.now()) / 1000);
      if (remaining > 0) {
        setLockoutTime(remaining);
        setError('Too many attempts. Please try again in 1 minute.');
        return;
      }
    }

    // 2. Sliding window rate limit check
    const activeAttempts = checkRateLimit();
    if (activeAttempts.length >= 5) {
      const lockoutUntil = Date.now() + 60000;
      localStorage.setItem('auth_lockout_until', lockoutUntil.toString());
      setLockoutTime(60);
      setError('Too many attempts. Please try again in 1 minute.');
      return;
    }

    // 3. Input validation
    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    if (!trimmedEmail || !trimmedPassword) {
      setError('All fields are required and cannot be empty spaces.');
      return;
    }

    // 4. Email format validation (Regex: /^[^\s@]+@(gmail\.com|outlook\.com|hotmail\.com|yahoo\.com|icloud\.com)$/i)
    const emailRegex = /^[^\s@]+@(gmail\.com|outlook\.com|hotmail\.com|yahoo\.com|icloud\.com|cryptoterminal\.com)$/i;
    if (!emailRegex.test(trimmedEmail)) {
      setError('Please provide a valid email address (gmail.com, outlook.com, hotmail.com, yahoo.com, icloud.com or cryptoterminal.com).');
      return;
    }

    // 5. Password validation
    if (trimmedPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    // Track the current attempt
    const updatedAttempts = [...activeAttempts, Date.now()];
    localStorage.setItem('auth_attempts', JSON.stringify(updatedAttempts));

    // Double check if this attempt triggers a lockout
    if (updatedAttempts.length >= 5) {
      const lockoutUntil = Date.now() + 60000;
      localStorage.setItem('auth_lockout_until', lockoutUntil.toString());
      setLockoutTime(60);
      setError('Too many attempts. Please try again in 1 minute.');
      return;
    }

    setLoading(true);
    try {
      await login(trimmedEmail, trimmedPassword);
      toast.success('Welcome back!');
      navigate('/terminal');
    } catch (err) {
      if (err.response?.status === 429) {
        const retryAfter = err.response.data.retryAfter || 60;
        const lockoutUntil = Date.now() + (retryAfter * 1000);
        localStorage.setItem('auth_lockout_until', lockoutUntil.toString());
        setLockoutTime(retryAfter);
      }
      setError(err.response?.data?.error || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-hex-bg" />
      <nav className="landing-nav">
        <div className="landing-nav-inner">
          <Link to="/" className="landing-logo">
            <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
              <rect width="28" height="28" rx="6" fill="#FECC58"/>
              <path d="M8 18L12 10L16 15L20 8" stroke="#000" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>CryptoTerminal</span>
          </Link>
          <div className="landing-nav-links">
            <Link to="/login" className="landing-nav-link">Log In</Link>
            <Link to="/signup" className="landing-btn-nav">Get Started</Link>
          </div>
        </div>
      </nav>

      <div className="auth-container">
        <div className="auth-card">
          <div className="auth-card-header">
            <h1>Welcome Back</h1>
            <p>Log in to access your trading terminal</p>
          </div>
          {error && <div className="auth-error">{error}</div>}
          <form onSubmit={handleSubmit} className="auth-form">
            <div className="auth-field">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={lockoutTime > 0}
              />
            </div>
            <div className="auth-field">
              <label>Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                disabled={lockoutTime > 0}
              />
            </div>
            <button
              type="submit"
              className="auth-btn"
              disabled={loading || lockoutTime > 0}
            >
              {lockoutTime > 0
                ? `Please wait ${lockoutTime}s`
                : loading
                ? 'Logging in...'
                : 'Log In'}
            </button>
          </form>
          <div className="auth-switch">
            Don't have an account? <Link to="/signup">Sign Up</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
